import { Injectable, inject } from '@angular/core';
import { ApiGatewayService } from '@cropx-web/services/api-gateway/ApiGatewayService';
import { Farm } from '@cropx-web/services/api-gateway/CompaniesApiService';
import {
  DacomFarmDetail,
  UserFarmsResponse,
} from '@cropx-web/services/api-gateway/CroprUserApiService';
import {
  Action,
  Selector,
  State,
  StateContext,
  createPropertySelectors,
} from '@ngxs/store';
import { Observable, tap } from 'rxjs';

import { FarmScopeSelectors } from './farm-scope.state';

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace Dacom {
  const type = 'Dacom';

  export class FetchFarms {
    static readonly type = `[${type}] FetchFarms`;

    constructor(public userUuid: string) {}
  }

  export class SetActivePlanUuid {
    static readonly type = `[${type}] Set Active PlanUuid`;

    constructor(public activePlanUuid: string | null) {}
  }
}

export interface DacomStateModel {
  farms: DacomFarmDetail[];
  farmsLoading: boolean;
  activePlanUuid: string;
}

@State<DacomStateModel>({
  name: 'dacom',
  defaults: {
    farms: [],
    farmsLoading: false,
    activePlanUuid: null,
  },
})
@Injectable()
export class DacomState {
  private api = inject(ApiGatewayService);

  @Action(Dacom.FetchFarms)
  fetchUserFarms(
    ctx: StateContext<DacomStateModel>,
    action: Dacom.FetchFarms,
  ): Observable<UserFarmsResponse> {
    ctx.patchState({ farmsLoading: true });
    return this.api.croprUser.getFarms(action.userUuid).pipe(
      tap((resp) => {
        ctx.patchState({
          farmsLoading: false,
          farms: resp.farms,
        });
      }),
    );
  }

  @Action(Dacom.SetActivePlanUuid)
  setActivePlanUuid(
    ctx: StateContext<DacomStateModel>,
    action: Dacom.SetActivePlanUuid,
  ) {
    ctx.patchState({
      activePlanUuid: action.activePlanUuid,
    });
  }
}

export class DacomSelectors {
  static slices = createPropertySelectors<DacomStateModel>(DacomState);

  @Selector([DacomSelectors.slices.farms, FarmScopeSelectors.slices.farmUuid])
  static activeFarm(
    farms: DacomFarmDetail[],
    farmUuid: string,
  ): DacomFarmDetail {
    return farms.find((f) => f.farmUuid === farmUuid);
  }

  @Selector([FarmScopeSelectors.farm, DacomSelectors.activeFarm])
  static isDacomFarm(FfsFarm: Farm, dacomFarm: DacomFarmDetail): boolean {
    if (!dacomFarm || dacomFarm.plans.length < 1) {
      return false;
    }

    if (!FfsFarm) {
      return false;
    }

    return FfsFarm.dataOrigin === 'DACOM';
  }
}
